import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Alert, Box, Button, FormControlLabel, Grid, Switch, Typography } from "@mui/material";
import Loading from "components/Loading";
import DashboardPageHeader from "components/common/DashboardPageHeader";
import DataLoadErrorAlert from "components/common/DataLoadErrorAlert";
import { Add } from "@mui/icons-material";
import { normalizePartnerTier } from "constants/partnerTiers";
import { useAuth } from "hooks/useAuth";
import { usePromptsManager } from "hooks/usePromptsManager";
import { PROMPT_LAYER_LABELS } from "services/llm/hierarchyPromptScope";
import {
  getHttpsCallableErrorMessage,
  listFusionPromptEditPermissions,
  setFusionPromptEditPermission,
} from "services/partnerTierService";
import { LLMManagerProvider } from "pages/dashboard/LLMManagerContext";
import GraphMonthlyUsageChartmind from "components/dashboard/admin/Graphs/GraphMonthlyUsageChartmind";
import GraphOrganizationStorageUsage from "components/dashboard/admin/Graphs/GraphOrganizationStorageUsage";
import { FDE_ROLE_LABELS } from "services/fde/fdeRegistryService";
import PromptCard from "./components/PromptCard";
import PromptEditorDialog from "./components/PromptEditorDialog";
import { groupChartMind7PromptRows } from "./chartMind7PromptGroups";

const UNCATEGORIZED_LABEL = "Uncategorized";

const groupPromptsByCategory = (rows) => {
  const map = new Map();
  for (const p of rows) {
    const raw = p.category && String(p.category).trim();
    const key = raw || UNCATEGORIZED_LABEL;
    if (!map.has(key)) map.set(key, []);
    map.get(key).push(p);
  }
  const keys = Array.from(map.keys()).sort((a, b) => {
    if (a === UNCATEGORIZED_LABEL) return 1;
    if (b === UNCATEGORIZED_LABEL) return -1;
    return a.localeCompare(b);
  });
  return keys.map((category) => ({ category, items: map.get(category) }));
};

const PromptsManagerContent = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const escalateTci = searchParams.get("escalateTci");
  const {
    user,
    userData,
    isGlobalAdmin,
    isRegionalAdmin,
    organizationId,
    organization,
    organizationLoading,
  } = useAuth();
  const regionScope = String(userData?.region || "").trim() || null;
  const canManageAsGlobal = Boolean(isGlobalAdmin);
  const canManageAsOrg = userData?.role === "admin" && Boolean(organizationId);
  const canManageRegional = Boolean(isRegionalAdmin && regionScope);
  const {
    prompts,
    loading,
    saving,
    error,
    savePrompt,
    createPrompt,
    canView,
    isReadOnly,
    hierarchyAccess,
  } = usePromptsManager();

  const hierarchyLayerLabel = useMemo(() => {
    const layers = hierarchyAccess?.editableLayers || [];
    if (!layers.length) return null;
    return layers.map((level) => PROMPT_LAYER_LABELS[level]).join(", ");
  }, [hierarchyAccess?.editableLayers]);

  const [pendingOnly, setPendingOnly] = useState(false);
  const groupedPrompts = useMemo(() => groupChartMind7PromptRows(prompts), [prompts]);
  const visiblePrompts = useMemo(
    () => (pendingOnly ? groupedPrompts.filter((row) => row.pendingCount > 0) : groupedPrompts),
    [groupedPrompts, pendingOnly],
  );
  const sections = useMemo(() => groupPromptsByCategory(visiblePrompts), [visiblePrompts]);

  const [selectedPrompt, setSelectedPrompt] = useState(null);
  const [createMode, setCreateMode] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [fusionEditByPromptId, setFusionEditByPromptId] = useState({});
  const [fusionEditReady, setFusionEditReady] = useState(false);
  const [fusionEditLoadError, setFusionEditLoadError] = useState(false);
  const [fusionEditSavingId, setFusionEditSavingId] = useState(null);
  const [fusionEditSuccessId, setFusionEditSuccessId] = useState(null);
  const [fusionEditErrorByPromptId, setFusionEditErrorByPromptId] = useState({});

  const showFusionEditToggle =
    !organizationLoading &&
    !isReadOnly &&
    normalizePartnerTier(organization?.partnerTier) === "founding";

  const loadFusionEditPermissions = useCallback(async () => {
    if (!showFusionEditToggle) {
      setFusionEditByPromptId({});
      setFusionEditReady(false);
      setFusionEditLoadError(false);
      return;
    }
    setFusionEditReady(false);
    setFusionEditLoadError(false);
    try {
      const result = await listFusionPromptEditPermissions();
      const next = {};
      for (const row of result?.permissions || []) {
        if (row?.promptId) {
          next[row.promptId] = row.fusionEditAllowed !== false;
        }
      }
      setFusionEditByPromptId(next);
      setFusionEditReady(true);
    } catch {
      setFusionEditLoadError(true);
      setFusionEditReady(false);
    }
  }, [showFusionEditToggle]);

  useEffect(() => {
    loadFusionEditPermissions();
  }, [loadFusionEditPermissions]);

  const handleFusionEditChange = async (promptId, allowed) => {
    if (!promptId) return;
    setFusionEditSavingId(promptId);
    setFusionEditSuccessId(null);
    setFusionEditErrorByPromptId((prev) => {
      const next = { ...prev };
      delete next[promptId];
      return next;
    });
    try {
      await setFusionPromptEditPermission({ promptId, allowed });
      setFusionEditByPromptId((prev) => ({ ...prev, [promptId]: allowed }));
      setFusionEditSuccessId(promptId);
    } catch (err) {
      setFusionEditErrorByPromptId((prev) => ({
        ...prev,
        [promptId]: getHttpsCallableErrorMessage(
          err,
          "Could not update Fusion Partner edit permission.",
        ),
      }));
    } finally {
      setFusionEditSavingId(null);
    }
  };
  const openCreate = () => {
    setCreateMode(true);
    setSelectedPrompt({ id: "", category: "chartmind" });
    setDialogOpen(true);
  };

  const goSimulateChartMindEncounter = () => {
    const currentPath = window.location.pathname;
    const isOnSavedSession = currentPath.match(
      /\/dashboard\/chartmind\/[^/]+$/
    );
    navigate("/dashboard/chartmind", {
      state: {
        key: Date.now(),
        reportingOrganizationId: organizationId || null,
      },
      ...(isOnSavedSession ? { replace: true } : {}),
    });
  };

  const openEdit = (prompt) => {
    setCreateMode(false);
    setSelectedPrompt(prompt);
    setDialogOpen(true);
  };

  const handleCloseEditor = () => setDialogOpen(false);

  const handleDialogExited = () => {
    setSelectedPrompt(null);
    setCreateMode(false);
  };

  const handleSubmit = async ({ id, updates, reason = null }) => {
    if (createMode) {
      await createPrompt({
        id,
        data: updates,
        reason,
        actorUid: user?.uid,
        actorEmail: user?.email || null,
      });
    } else {
      await savePrompt({
        id,
        updates,
        reason,
        actorUid: user?.uid,
        actorEmail: user?.email || null,
      });
    }
  };

  if (!canView) {
    return (
      <Alert severity="warning">
        You do not have permission to manage prompts.
      </Alert>
    );
  }

  return (
    <Box>
      <DashboardPageHeader
        title="Prompts"
        subtitle={
          isReadOnly
            ? `Read-only prompt view for ${FDE_ROLE_LABELS["fde-t"]} review.`
            : canManageRegional
              ? `Regional prompts for region "${regionScope}". Embedded / clinical tabs are UI-only until wired to storage.`
              : hierarchyLayerLabel
                ? `${hierarchyLayerLabel} settings.`
                : "Manage AI prompt behavior for your organization."
        }
        actions={
          canManageAsGlobal || canManageAsOrg ? (
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: 1,
                justifyContent: { xs: "flex-start", sm: "flex-end" },
              }}
            >
              <Button
                variant="outlined"
                onClick={goSimulateChartMindEncounter}
              >
                Simulate a ChartMind encounter
              </Button>
              {canManageAsGlobal && (
                <Button startIcon={<Add />} variant="contained" onClick={openCreate}>
                  New Prompt
                </Button>
              )}
            </Box>
          ) : null
        }
      />

      {escalateTci ? (
        <Alert
          severity="warning"
          sx={{ mt: 2 }}
          onClose={() => {
            const next = new URLSearchParams(searchParams);
            next.delete("escalateTci");
            setSearchParams(next, { replace: true });
          }}
        >
          TCI Manager escalated stalled TCI <strong>{escalateTci}</strong> for{" "}
          {FDE_ROLE_LABELS["fde-t"]} review.
          Check scoped settings and readiness for structural prompt issues beyond
          candidate promotion.
        </Alert>
      ) : null}

      {isReadOnly ? (
        <Alert severity="info" sx={{ mt: 2 }}>
          Institution settings are read-only. Additional scoped settings appear when your
          assignment includes them.
        </Alert>
      ) : null}

      {error ? (
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
      ) : null}

      {showFusionEditToggle && fusionEditLoadError ? (
        <Box sx={{ mt: 2 }}>
          <DataLoadErrorAlert onRetry={loadFusionEditPermissions} />
        </Box>
      ) : null}

      {organization?.stalePromptFlags &&
      Object.keys(organization.stalePromptFlags).length > 0 ? (
        <Alert severity="warning" sx={{ mt: 2 }}>
          Some prompt settings were written for an older global prompt version. Review
          and update: {Object.keys(organization.stalePromptFlags).join(", ")}.
        </Alert>
      ) : null}

      {loading ? (
        <Loading page />
      ) : (
        <Box sx={{ mt: 3 }}>
          <FormControlLabel
            control={
              <Switch
                checked={pendingOnly}
                onChange={(_, checked) => setPendingOnly(checked)}
                inputProps={{ "aria-label": "Pending review only" }}
              />
            }
            label="Pending review only"
          />
          {sections.map(({ category, items }) => (
            <Box key={category} sx={{ mb: 4 }}>
              <Typography
                variant="overline"
                color="text.secondary"
                sx={{
                  display: "block",
                  letterSpacing: "0.12em",
                  fontWeight: 700,
                  mb: 1.5,
                }}
              >
                {category}
              </Typography>
              <Grid container spacing={2}>
                {items.map((prompt) => (
                  <Grid key={prompt.id} item xs={12} md={6} lg={4}>
                    <PromptCard
                      prompt={prompt}
                      onEdit={openEdit}
                      readOnly={isReadOnly}
                      showFusionEditToggle={showFusionEditToggle && !fusionEditLoadError}
                      fusionEditAllowed={fusionEditByPromptId[prompt.id] !== false}
                      fusionEditSaving={fusionEditSavingId === prompt.id}
                      fusionEditReady={fusionEditReady}
                      fusionEditSuccess={fusionEditSuccessId === prompt.id}
                      fusionEditError={fusionEditErrorByPromptId[prompt.id] || ""}
                      onFusionEditChange={(allowed) =>
                        handleFusionEditChange(prompt.id, allowed)
                      }
                      onDismissFusionEditSuccess={() => setFusionEditSuccessId(null)}
                      onDismissFusionEditError={() =>
                        setFusionEditErrorByPromptId((prev) => {
                          const next = { ...prev };
                          delete next[prompt.id];
                          return next;
                        })
                      }
                    />
                  </Grid>
                ))}
              </Grid>
            </Box>
          ))}
          {!error && !prompts.length ? (
            <Alert severity="info">
              No prompts found. This can be normal in a new environment with no
              `llmQueryConfig` documents yet.
            </Alert>
          ) : null}
          {organizationId ? (
            <Box sx={{ mt: 6 }}>
              <GraphMonthlyUsageChartmind organizationId={organizationId} />
            </Box>
          ) : null}
          {organizationId ? (
            <Box sx={{ mt: 6 }}>
              <GraphOrganizationStorageUsage organizationId={organizationId} />
            </Box>
          ) : null}
        </Box>
      )}

      <PromptEditorDialog
        open={!isReadOnly && dialogOpen}
        prompt={selectedPrompt}
        createMode={createMode}
        saving={saving}
        canEditGlobal={canManageAsGlobal}
        canEditOrganization={canManageAsOrg && !canManageAsGlobal}
        canEditRegional={canManageRegional && !canManageAsGlobal}
        canEditP4={Boolean(hierarchyAccess?.canEditP4)}
        canEditP5={Boolean(hierarchyAccess?.canEditP5)}
        canEditP6={Boolean(hierarchyAccess?.canEditP6)}
        p4ScopeId={hierarchyAccess?.p4ScopeId || null}
        p5ScopeId={hierarchyAccess?.p5ScopeId || null}
        p6ScopeId={hierarchyAccess?.p6ScopeId || null}
        onClose={handleCloseEditor}
        onExited={handleDialogExited}
        onSubmit={handleSubmit}
      />
    </Box>
  );
};

const PromptsManagerPage = () => {
  const { user, userData, isGlobalAdmin } = useAuth();

  const managerUser = useMemo(
    () => ({
      ...(userData || {}),
      uid: user?.uid,
      admin: Boolean(isGlobalAdmin),
    }),
    [userData, user?.uid, isGlobalAdmin],
  );

  return (
    <LLMManagerProvider user={managerUser}>
      <PromptsManagerContent />
    </LLMManagerProvider>
  );
};

export default PromptsManagerPage;
